import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import {
  trackViewContent,
  trackAddToCart,
  makeVariantSku,
} from "@/lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const img = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, dragFree: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${slug}`);
        setProduct(res.data);
        const cs = res.data?.colors || [];
        if (cs.length > 0) setColor(cs[0].name);
      } catch (e) {
        console.error("[product-page] failed to load product:", e);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Whenever colour changes, snap carousel back to the first image of that colour
  useEffect(() => {
    if (emblaApi) emblaApi.scrollTo(0);
  }, [color, emblaApi]);

  // Fire Meta Pixel ViewContent when product/variant changes
  useEffect(() => {
    if (!product) return;
    trackViewContent({
      content_ids: [makeVariantSku(product.id, color, size)],
      content_name: product.name,
      value: product.price_usd,
      currency: "AUD",
    });
  }, [product, color, size]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const colorOptions = product?.colors || [];
  const hasMultipleColors = colorOptions.length > 1;
  const activeColor = hasMultipleColors
    ? colorOptions.find((c) => c.name === color)
    : null;
  const displayImages = activeColor?.images?.length
    ? activeColor.images
    : product?.images || [];

  const handleAdd = () => {
    if (!product) return;
    if (!size) {
      toast.error("Pick a size first.");
      return;
    }
    if (hasMultipleColors && !color) {
      toast.error("Pick a colour first.");
      return;
    }
    addItem(product, size, 1, hasMultipleColors ? color : null);
    trackAddToCart({
      content_ids: [makeVariantSku(product.id, hasMultipleColors ? color : null, size)],
      content_name: product.name,
      value: product.price_usd,
      currency: "AUD",
    });
    const label = hasMultipleColors ? `${product.name} — ${color} (${size})` : `${product.name} (${size})`;
    toast.success(`${label} added.`);
  };

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-10 grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="product-loading">
        <div className="aspect-square border-4 border-[#1C1914] bg-[#FFFDF5] animate-pulse" />
        <div className="space-y-4">
          <div className="h-12 w-3/4 bg-[#1C1914]/10 animate-pulse" />
          <div className="h-6 w-1/2 bg-[#1C1914]/10 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="px-4 md:px-8 py-20 text-center" data-testid="product-not-found">
        <p className="font-display text-5xl uppercase">Lost it.</p>
        <p className="font-body text-base mt-3">That tee is hiding from us. Probably for the best.</p>
        <Link to="/" className="btn-duds inline-block mt-6">Back to shop</Link>
      </div>
    );
  }

  return (
    <div className="pb-32 md:pb-16" data-testid="product-page">
      <div className="px-4 md:px-8 py-6 border-b-2 border-dashed border-[#1C1914]/40">
        <Link to="/" className="font-condensed uppercase tracking-widest text-sm hover:text-[#D95D39]" data-testid="product-back-link">
          ← Back to shop
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-10 px-0 lg:px-8 py-0 lg:py-10">
        {/* Image carousel — full bleed on mobile */}
        <div className="relative border-b-4 lg:border-4 border-[#1C1914] bg-[#FFFDF5] overflow-hidden">
          <div className="embla overflow-hidden" ref={emblaRef} data-testid="product-image-carousel">
            <div className="flex">
              {displayImages.map((src, i) => (
                <div key={`${color || "default"}-${i}`} className="flex-[0_0_100%] aspect-square">
                  <img
                    src={img(src)}
                    alt={`${product.name} view ${i + 1}`}
                    className="w-full h-full object-cover select-none"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
          {displayImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={scrollPrev}
                className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 bg-[#FFFDF5] border-2 border-[#1C1914] p-2 hover:bg-[#FDE047]"
                aria-label="Previous image"
                data-testid="product-image-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={scrollNext}
                className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 bg-[#FFFDF5] border-2 border-[#1C1914] p-2 hover:bg-[#FDE047]"
                aria-label="Next image"
                data-testid="product-image-next"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="product-image-dots">
                {displayImages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => emblaApi?.scrollTo(i)}
                    aria-label={`Go to image ${i + 1}`}
                    className={`w-2.5 h-2.5 border border-[#1C1914] ${
                      selectedIndex === i ? "bg-[#1C1914]" : "bg-[#FFFDF5]"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Actual-size scale indicator (only when product specifies one) */}
          {product.scale_indicator_cm && (
            <div
              className="absolute top-3 right-3 bg-[#FFFDF5] border-2 border-[#1C1914] px-2 py-1.5 shadow-hard-sm"
              data-testid="product-scale-indicator"
              aria-label={`Embroidery patch is approximately ${product.scale_indicator_cm} centimetres wide, shown actual size`}
            >
              <p className="font-condensed text-[10px] uppercase tracking-widest text-[#1C1914]/80 leading-none mb-1">
                Patch · actual size
              </p>
              <div className="flex items-end gap-1.5">
                {/* Ruler — width is in real-world cm. Browsers render `cm` at ~96 CSS px/inch. */}
                <div
                  className="relative border-b-2 border-[#1C1914]"
                  style={{ width: `${product.scale_indicator_cm}cm`, height: "10px" }}
                  aria-hidden="true"
                >
                  {/* Tick marks every 1cm */}
                  {Array.from({ length: Math.floor(product.scale_indicator_cm) + 1 }).map((_, i) => (
                    <span
                      key={i}
                      className="absolute bottom-0 w-px bg-[#1C1914]"
                      style={{
                        left: `${(i / product.scale_indicator_cm) * 100}%`,
                        height: i % 2 === 0 ? "10px" : "5px",
                      }}
                    />
                  ))}
                </div>
                <span className="font-display text-sm leading-none">
                  ≈{product.scale_indicator_cm}cm
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Product info */}
        <div className="px-4 md:px-0 py-6 lg:py-2 space-y-6">
          <div>
            <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]" data-testid="product-status">
              {product.status}
            </p>
            <h1 className="font-display text-5xl md:text-6xl uppercase leading-none mt-2" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-condensed text-xl md:text-2xl uppercase tracking-wide text-[#4A5D23] mt-3 leading-tight" data-testid="product-tagline">
              {product.tagline}
            </p>
          </div>

          <div className="flex items-baseline gap-4">
            <span className="font-display text-4xl md:text-5xl" data-testid="product-price">
              A${product.price_usd.toFixed(0)}
            </span>
            <span className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/70">AUD</span>
          </div>

          <p className="font-body text-base md:text-lg leading-relaxed text-[#1C1914]/85" data-testid="product-description">
            {product.description}
          </p>

          {/* Colour picker (only when product has multiple colours) */}
          {hasMultipleColors && (
            <div data-testid="product-color-picker">
              <div className="flex items-center justify-between">
                <p className="font-condensed text-sm uppercase tracking-widest">
                  Colour {color && <span className="text-[#1C1914]/60">— {color}</span>}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {colorOptions.map((c) => {
                  const active = color === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setColor(c.name)}
                      className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 border-2 transition-colors ${
                        active
                          ? "border-[#1C1914] bg-[#1C1914] text-[#FFFDF5]"
                          : "border-[#1C1914] bg-[#FFFDF5] hover:bg-[#FDE047]"
                      }`}
                      data-testid={`product-color-${c.name.toLowerCase()}`}
                      aria-pressed={active}
                      aria-label={`Colour ${c.name}`}
                    >
                      <span
                        aria-hidden="true"
                        className="inline-block w-6 h-6 border-2 border-[#1C1914]"
                        style={{ background: c.chip || "#fff" }}
                      />
                      <span aria-hidden="true" className="font-condensed text-sm uppercase tracking-widest">
                        {c.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Size picker */}
          <div data-testid="product-size-picker">
            <div className="flex items-center justify-between">
              <p className="font-condensed text-sm uppercase tracking-widest">Size</p>
              <button className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/60 underline">
                Size guide
              </button>
            </div>
            <div className="grid grid-cols-6 gap-2 mt-3">
              {product.variants.map((v) => {
                const active = size === v.size;
                return (
                  <button
                    key={v.size}
                    type="button"
                    onClick={() => setSize(v.size)}
                    disabled={!v.in_stock}
                    className={`py-3 border-2 font-display text-base uppercase tracking-wider transition-colors ${
                      active
                        ? "bg-[#1C1914] text-[#FFFDF5] border-[#1C1914]"
                        : "border-[#1C1914] hover:bg-[#FDE047]"
                    } ${!v.in_stock ? "opacity-30 cursor-not-allowed line-through" : ""}`}
                    data-testid={`product-size-${v.size}`}
                  >
                    {v.size}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Add to cart (desktop) */}
          <button
            type="button"
            onClick={handleAdd}
            className="btn-duds w-full hidden md:inline-flex"
            data-testid="product-add-to-cart-desktop"
          >
            Add to bag — A${product.price_usd.toFixed(0)}
          </button>

          <div className="border-t-2 border-dashed border-[#1C1914]/40 pt-4 space-y-1.5">
            {product.features.map((f, i) => (
              <p key={i} className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/80">
                · {f}
              </p>
            ))}
            {!hasMultipleColors && (
              <p className="font-condensed text-sm uppercase tracking-widest text-[#1C1914]/80">
                · Shirt colour: {product.shirt_color}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Sticky add-to-cart bar — mobile only */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#FFFDF5] border-t-4 border-[#1C1914] px-4 py-3 flex items-center gap-3"
        data-testid="product-sticky-bar"
      >
        <div className="flex-shrink-0">
          <div className="font-display text-2xl leading-none">A${product.price_usd.toFixed(0)}</div>
          <div className="font-condensed text-[10px] uppercase tracking-widest text-[#1C1914]/70">
            {size ? `Size ${size}` : "Pick a size"}
          </div>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="btn-duds flex-1 !py-3"
          data-testid="product-add-to-cart-mobile"
        >
          Add to bag
        </button>
      </div>
    </div>
  );
};

export default ProductPage;
